import { useEffect } from "react";
import { Page, PageStatus } from "src/components";
import { useModals } from "src/components/Modals";
import { Button, Color, Flex, SubgridTable, Text } from "src/elements";
import { RouteGetUsersList, User } from "@CommonApi/users/list";
import { useLoaders } from "src/components/InlineLoader";
import { useUsersPage } from "./store/hooks";
import { useUser } from "src/atoms/user";
import { hasPermission, PermissionCode, RoleCode } from "@CommonUtils/RolesPermissions";
import { useMenu } from "src/components/Menu";
import { useEditUserModal } from "src/modals/EditUser/store/hooks";
import { useResetUserPasswordModal } from "src/modals/ResetUserPassword/store/hooks";

export const PageUsers = () => {
  const { openModal, openConfirmModal } = useModals();
  const { hasLoader } = useLoaders();
  const { openMenu } = useMenu();
  const { openEditUserModal } = useEditUserModal();
  const { openResetUserPasswordModal } = useResetUserPasswordModal();

  const { user } = useUser();
  const { users, loadUsers, deleteUser } = useUsersPage();
  const isUsersLoading = users === undefined || hasLoader(RouteGetUsersList);

  const canCreate = hasPermission(user.roleCode, PermissionCode.USERS_CREATE);
  const canUpdate = hasPermission(user.roleCode, PermissionCode.USERS_UPDATE);
  const canDelete = hasPermission(user.roleCode, PermissionCode.USERS_DELETE);

  useEffect(() => {
    loadUsers();
  }, []);

  const openDeleteConfirm = (targetUser: User) => {
    openConfirmModal({
      title: `Are you sure you want to delete ${targetUser.firstName} ${targetUser.lastName}?`,
      icon: { name: "alert", color: Color.red },
      buttons: [
        { id: "cancel", label: "Cancel", color: "primary", variant: "inverted" },
        { id: "delete", label: "Delete", color: "red", onClick: () => deleteUser(targetUser.id) },
      ],
    });
  };

  const openActionsMenu = (event: React.MouseEvent<HTMLButtonElement>, targetUser: User) => {
    const options = [];

    if (canUpdate) {
      options.push({
        label: "Edit",
        icon: { name: "edit" as const, color: Color.primary },
        onClick: () => openEditUserModal(targetUser),
      });

      options.push({
        label: "Reset Password",
        icon: { name: "locked" as const, color: Color.primary },
        onClick: () => openResetUserPasswordModal(targetUser),
      });
    }

    if (canDelete && targetUser.roleCode !== RoleCode.ADMIN) {
      options.push({
        label: "Delete",
        icon: { name: "delete" as const, color: Color.red },
        onClick: () => openDeleteConfirm(targetUser),
      });
    }

    if (options.length === 0) return;

    openMenu({ event, menu: { options } });
  };

  const hasAnyRowAction = canUpdate || canDelete;

  return (
    <Page>
      <Flex padding="10px 0 0 0" justify="between" align="center">
        <Text variant="h3">Users</Text>

        {canCreate && <Button onClick={() => openModal("AddUserModal")}>Add user</Button>}
      </Flex>

      {!users && isUsersLoading && <PageStatus label="Loading users..." icon="spinner" />}

      {users === null && <PageStatus label="Users not found" icon="alert" color={Color.red} />}

      {users && (
        <SubgridTable
          columns={[
            { id: "id", header: "ID", width: "80px" },
            { id: "firstName", header: "First name", width: "1fr" },
            { id: "lastName", header: "Last name", width: "1fr" },
            { id: "email", header: "E-mail", width: "1fr" },
            { id: "roleCode", header: "Role", width: "1fr" },
            ...(hasAnyRowAction
              ? [
                  {
                    id: "actions",
                    header: "",
                    width: "60px",
                    renderer: ({ row: targetUser }: { row: User }) => (
                      <Button
                        variant="borderless"
                        iconRight={{ name: "more" }}
                        onClick={(event) => openActionsMenu(event, targetUser)}
                      />
                    ),
                  },
                ]
              : []),
          ]}
          data={users}
        />
      )}
    </Page>
  );
};
